import React, { useEffect, useState } from 'react'
import { useParams } from 'react-router-dom';
import Configurator from '../../Reusables/Configurator/Configurator';
import { fetchProductDetails } from '../../../utils/api-service/get-services';
import { FaBox, FaRupeeSign, FaSpinner, FaList } from 'react-icons/fa';
import ProductPriceBreakdown from '../../Reusables/ProductPriceBreakdown/ProductPriceBreakdown';
import { FaCoins, FaDollarSign } from 'react-icons/fa6';

const ConfiguratorPage = () => {
    const { id } = useParams();
    const [price, setPrice] = useState("");
    const [priceComponents, setPriceComponents] = useState();
    const [priceLoading, setPriceLoading] = useState(false);
    const [productDetails, setProductDetails] = useState({});
    const [priceType, setPriceType] = useState("selling");

    const getProductDetails = async () => {
        try {
            const response = await fetchProductDetails(id);
            if (response && response.success !== false) {
                setProductDetails(response);
            }
        } catch (error) {
            console.log("Error: ", error);
        }
    }

    useEffect(() => {
        if (id) {
            getProductDetails();
        }
    }, [id])

    const onCalculatePricing = (calculatedPrice, priceComponents) => {
        setPrice(calculatedPrice);
        setPriceComponents(priceComponents);
    }

    const setProductPriceLoading = (loading) => {
        setPriceLoading(loading);
    }

    const formatCurrency = (amount, currency = 'INR') => {
        const formatters = {
            'INR': new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', minimumFractionDigits: 0, maximumFractionDigits: 2 }),
            'USD': new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 2 }),
            'GBP': new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP', minimumFractionDigits: 2 }),
            'EUR': new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR', minimumFractionDigits: 2 }),
            'AED': new Intl.NumberFormat('ar-AE', { style: 'currency', currency: 'AED', minimumFractionDigits: 2 }),
            'SAR': new Intl.NumberFormat('ar-SA', { style: 'currency', currency: 'SAR', minimumFractionDigits: 2 })
        };

        return formatters[currency]?.format(amount) || `${currency} ${amount.toFixed(2)}`;
    };

    return (
        <div className="container-fluid px-4 py-4">
            <div className="row g-4">
                {/* Left Column - Product Info & Configurator */}
                <div className="col-lg-5">
                    {/* Product Info Card */}
                    <div className="card shadow-sm border-0 rounded-3 mb-4">
                        <div className="card-body p-4">
                            <h5 className="card-title text-primary mb-3">
                                <FaBox className="me-2" />
                                Product Details
                            </h5>
                            <div className="mb-4">
                                <div className="btn-group w-100" role="group" aria-label="Price type selector">
                                    <button
                                        type="button"
                                        className={`btn ${priceType === 'cost' ? 'btn-primary' : 'btn-outline-secondary'} d-flex align-items-center justify-content-center gap-2 flex-fill`}
                                        onClick={() => setPriceType("cost")}
                                        style={{
                                            borderRadius: '0.375rem 0 0 0.375rem',
                                            transition: 'all 0.2s ease'
                                        }}
                                    >
                                        <FaCoins size={14} />
                                        <span>Cost Price</span>
                                    </button>
                                    <button
                                        type="button"
                                        className={`btn ${priceType === 'selling' ? 'btn-primary' : 'btn-outline-secondary'} d-flex align-items-center justify-content-center gap-2 flex-fill`}
                                        onClick={() => setPriceType("selling")}
                                        style={{
                                            borderRadius: '0 0.375rem 0.375rem 0',
                                            transition: 'all 0.2s ease'
                                        }}
                                    >
                                        <FaDollarSign size={14} />
                                        <span>Selling Price</span>
                                    </button>
                                </div>
                                <div className="mt-2 text-center">
                                    <small className="text-muted">
                                        {priceType === 'cost' ? 'Showing cost price breakdown' : 'Showing selling price breakdown'}
                                    </small>
                                </div>
                            </div>

                            <div className="border-bottom pb-3 mb-3">
                                <div className="d-flex justify-content-between align-items-center">
                                    <span className="text-muted small text-uppercase">Product Name</span>
                                    <span className="fw-semibold">{productDetails?.name || '—'}</span>
                                </div>
                            </div>

                            <div className="border-bottom pb-3 mb-3">
                                <div className="d-flex justify-content-between align-items-center">
                                    <span className="text-muted small text-uppercase">Product Code</span>
                                    <span className="fw-semibold">{productDetails?.code || '—'}</span>
                                </div>
                            </div>

                            <div className="pb-2">
                                <div className="d-flex justify-content-between align-items-center">
                                    <span className="text-muted small text-uppercase">Total Price</span>
                                    <div className="text-end">
                                        {priceLoading ? (
                                            <span className="text-muted">
                                                <FaSpinner className="fa-spin me-2" />
                                                Calculating...
                                            </span>
                                        ) : (
                                            <span className="h5 mb-0 fw-bold text-success">
                                                {price || '—'}
                                            </span>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Configurator */}
                    <div className="card shadow-sm border-0 rounded-3">
                        <div className="card-body p-4">
                            <Configurator
                                id={id}
                                onPriceCalculated={onCalculatePricing}
                                onCalculating={setProductPriceLoading}
                                priceType={priceType}
                            />
                        </div>
                    </div>
                </div>

                {/* Right Column - Price Components */}
                <div className="col-lg-7">
                    {/* Price Breakdown Header */}
                    <div className="d-flex align-items-center mb-4">
                        <h5 className="mb-0 fw-bold text-primary">
                            <FaRupeeSign className="me-2" />
                            Price Breakdown
                        </h5>
                        <div className="ms-3 flex-grow-1">
                            <hr className="my-0" />
                        </div>
                        {priceComponents && (
                            <span className="badge bg-success ms-3">
                                {Object.keys(priceComponents).reduce((total, key) => {
                                    if (key === 'inrComponents' || key === 'localComponents') {
                                        return total + Object.keys(priceComponents[key] || {}).length;
                                    }
                                    return total;
                                }, 0)} Components
                            </span>
                        )}
                    </div>

                    {/* All Components */}
                    {priceComponents && (
                        <ProductPriceBreakdown
                            priceComponent={priceComponents}
                            priceTypeEnabled={false}
                        />
                    )}

                    {/* Empty State */}
                    {!priceComponents && !priceLoading && (
                        <div className="text-center py-5">
                            <div className="display-4 text-muted mb-3">📊</div>
                            <h5 className="text-muted">No Price Components</h5>
                            <p className="text-muted small">
                                Configure your product to see the price breakdown
                            </p>
                        </div>
                    )}

                    {/* Loading State */}
                    {priceLoading && (
                        <div className="text-center py-5">
                            <FaSpinner className="fa-spin text-primary" size={40} />
                            <p className="mt-3 text-muted">Loading price breakdown...</p>
                        </div>
                    )}
                </div>
            </div>
        </div>
    )
}

export default ConfiguratorPage